import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { ShowHideButton } from '@/components/show-hide';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';

import { useToggle } from '@/hooks/use-toggle';

import type { ForgotPasswordConfirmFormValues } from '@/schemas/auth.schema';

import { useFormikError } from '@/lib/error';

import { ForgotPasswordConfirmFormProvider } from '@/formik/auth.formik';

interface ForgotPasswordConfirmProps {
  isLoading: boolean;
  onSubmit: (values: ForgotPasswordConfirmFormValues) => void;
  onBack: () => void;
}

const SubmitButton = ({ isLoading }: { isLoading: boolean }) => {
  const { isValid } = useFormikContext();
  return (
    <Button type="submit" className="w-full" disabled={isLoading || !isValid}>
      Reset password
    </Button>
  );
};

const ConfirmForm = ({ isLoading, onBack }: { isLoading: boolean; onBack: () => void }) => {
  const { state: newPasswordState, toggle: toggleNewPassword } = useToggle();
  const { state: confirmNewPasswordState, toggle: toggleConfirmNewPassword } = useToggle();
  const { getFieldProps } = useFormikContext<ForgotPasswordConfirmFormValues>();
  const hasError = useFormikError();

  return (
    <div className="flex flex-col gap-4">
      <Field aria-invalid={hasError('confirmation_code')}>
        <RequiredLabel htmlFor="confirmation_code">Confirmation code</RequiredLabel>
        <Input
          id="confirmation_code"
          type="text"
          placeholder="Enter the code sent to your email"
          {...getFieldProps('confirmation_code')}
          aria-invalid={hasError('confirmation_code')}
        />
        <ErrorField name="confirmation_code" />
      </Field>

      <Field aria-invalid={hasError('new_password')}>
        <RequiredLabel htmlFor="new_password">New password</RequiredLabel>
        <InputGroup>
          <InputGroupInput
            type={newPasswordState ? 'text' : 'password'}
            id="new_password"
            placeholder="New password"
            {...getFieldProps('new_password')}
            aria-invalid={hasError('new_password')}
          />
          <InputGroupAddon align="inline-end">
            <ShowHideButton state={newPasswordState} toggle={toggleNewPassword} />
          </InputGroupAddon>
        </InputGroup>
        <ErrorField name="new_password" />
      </Field>

      <Field aria-invalid={hasError('confirm_new_password')}>
        <RequiredLabel htmlFor="confirm_new_password">Confirm password</RequiredLabel>
        <InputGroup>
          <InputGroupInput
            type={confirmNewPasswordState ? 'text' : 'password'}
            id="confirm_new_password"
            placeholder="Confirm new password"
            {...getFieldProps('confirm_new_password')}
            aria-invalid={hasError('confirm_new_password')}
          />
          <InputGroupAddon align="inline-end">
            <ShowHideButton state={confirmNewPasswordState} toggle={toggleConfirmNewPassword} />
          </InputGroupAddon>
        </InputGroup>
        <ErrorField name="confirm_new_password" />
      </Field>

      <SubmitButton isLoading={isLoading} />
      <Button type="button" variant="ghost" className="w-full" onClick={onBack}>
        Back
      </Button>
    </div>
  );
};

export const ForgotPasswordConfirm = ({
  isLoading,
  onSubmit,
  onBack,
}: ForgotPasswordConfirmProps) => {
  return (
    <>
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Reset your password</h1>
        <p className="text-muted-foreground text-sm text-balance">
          Enter the confirmation code sent to your email and set a new password.
        </p>
      </div>

      <div className="w-full max-w-xs mx-auto">
        <ForgotPasswordConfirmFormProvider onSubmit={onSubmit}>
          <ConfirmForm isLoading={isLoading} onBack={onBack} />
        </ForgotPasswordConfirmFormProvider>
      </div>
    </>
  );
};
